import {useEffect, useRef, useState} from "react";

const DEFAULT_DELAY = 300;
const DEFAULT_MINIMUM_VISIBLE = 150;

/**
 * Presentation-only smoothing for a busy flag.
 *
 * Nothing is shown until the work has lasted `delay`, so the common fast
 * request never flashes an indicator.
 *
 * `minimumVisible` holds the indicator for a while after the work finishes, to
 * stop a request that ends just past the threshold producing a one frame
 * blink. It defaults to 0 - it buys smoothness at the cost of making the whole
 * interaction feel slower than it is, which is only worth paying for a loud,
 * attention grabbing indicator, never for a thin progress bar.
 *
 * The redux flag itself stays truthful - only the visual lags. Anything that
 * must reflect the real request state (aria-busy, disabling a control) should
 * keep reading the original flag.
 */
export const useDelayedBusy = (
  busy,
  { delay = DEFAULT_DELAY, minimumVisible = DEFAULT_MINIMUM_VISIBLE } = {}
) => {
  const [visible, setVisible] = useState(false);
  const shownAt = useRef(null);

  useEffect(() => {
    if (busy) {
      const timer = setTimeout(() => {
        shownAt.current = Date.now();
        setVisible(true);
      }, delay);

      return () => clearTimeout(timer);
    }

    if (!visible) {
      return undefined;
    }

    const shownFor = shownAt.current ? Date.now() - shownAt.current : 0;
    const remaining = Math.max(0, minimumVisible - shownFor);
    const timer = setTimeout(() => {
      shownAt.current = null;
      setVisible(false);
    }, remaining);

    return () => clearTimeout(timer);
  }, [busy, visible, delay, minimumVisible]);

  return visible;
};

export default useDelayedBusy;
